import { useState, useMemo } from 'react'
import { colors, formatNumber } from '../styles/tokens'
import { Button, Input, Select } from './FormControls'
import { calculateFormwork } from '../lib/quantityEngine'
import { useDraftPersistence } from '../lib/useDraftPersistence'

export default function FormworkModulePanel({ scopeId, boqItemId, linkedItems, superseding, onClose, onError, saving, onSave }) {
  const draftKey = `draft:formwork:${scopeId || boqItemId || 'unknown'}`
  const [draft, setDraft, clearDraft] = useDraftPersistence(draftKey, { elements: [] })
  const initial = superseding ? { elements: superseding.engineering_inputs?.elements || [] } : draft

  const [elements, setElementsRaw] = useState(initial.elements)
  const setElements = (u) => setElementsRaw((p) => { const n = typeof u === 'function' ? u(p) : u; if (!superseding) setDraft((d) => ({ ...d, elements: n })); return n })

  function addElement() { setElements((p) => [...p, { id: `E${Date.now()}`, label: '', elementType: 'slab', shape: 'rectangular', length: '', width: '', depth: '', height: '', diameter: '', geometryCount: '', formedEdges: [{ edge: 'length_side_1', included: false }, { edge: 'length_side_2', included: false }, { edge: 'width_side_1', included: false }, { edge: 'width_side_2', included: false }] }]) }
  function updateElement(id, patch) { setElements((p) => p.map((e) => (e.id === id ? { ...e, ...patch } : e))) }
  function removeElement(id) { setElements((p) => p.filter((e) => e.id !== id)) }
  function toggleEdge(id, edge) { setElements((p) => p.map((e) => (e.id === id ? { ...e, formedEdges: e.formedEdges.map((fe) => (fe.edge === edge ? { ...fe, included: !fe.included } : fe)) } : e))) }

  const elementsForCalc = useMemo(() => elements.map((e) => ({
    label: e.label, elementType: e.elementType, shape: e.shape,
    length: e.length === '' ? NaN : Number(e.length), width: e.width === '' ? NaN : Number(e.width),
    depth: e.depth === '' ? NaN : Number(e.depth), height: e.height === '' ? NaN : Number(e.height),
    diameter: e.diameter === '' ? NaN : Number(e.diameter),
    geometryCount: e.geometryCount === '' ? undefined : Number(e.geometryCount),
    formedEdges: e.formedEdges,
  })), [elements])

  const result = useMemo(() => {
    if (elementsForCalc.length === 0) return { value: null, error: null }
    try { return { value: calculateFormwork(elementsForCalc), error: null } }
    catch (e) { return { value: null, error: e.message } }
  }, [elementsForCalc])

  async function handleSave() {
    if (!result.value) return
    const engineeringInputs = { elements }
    const succeeded = await onSave({
      calculatedValueTon: result.value.finalQuantity, unit: 'm2',
      geometrySnapshot: { items: [] }, engineeringInputs,
      breakdownSnapshot: { steps: result.value.steps, elementBreakdown: result.value.elementBreakdown },
      takeoffItemIdForProvenance: null,
    })
    if (succeeded !== false) clearDraft()
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
        <label style={{ fontSize: 11, fontWeight: 700, color: colors.textSecondary, textTransform: 'uppercase' }}>Formwork Elements</label>
        <Button size="sm" variant="secondary" onClick={addElement}>+ Add Element</Button>
      </div>
      {elements.map((e) => (
        <div key={e.id} style={{ border: `1px solid ${colors.border}`, borderRadius: 6, padding: 8, marginBottom: 8 }}>
          <div style={{ display: 'flex', gap: 6, marginBottom: 6 }}>
            <Input placeholder="Label" value={e.label} onChange={(ev) => updateElement(e.id, { label: ev.target.value })} style={{ flex: 1, fontSize: 11 }} />
            <button onClick={() => removeElement(e.id)} style={{ background: 'none', border: 'none', color: colors.danger, cursor: 'pointer' }}>✕</button>
          </div>
          <LabeledSelect label="Element Type" value={e.elementType} onChange={(ev) => updateElement(e.id, { elementType: ev.target.value })}>
            <option value="slab">Slab</option><option value="beam">Beam</option><option value="column">Column</option>
          </LabeledSelect>

          {e.elementType === 'slab' && (
            <>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 6 }}>
                <LabeledInput label="Length (m)" type="number" value={e.length} onChange={(ev) => updateElement(e.id, { length: ev.target.value })} />
                <LabeledInput label="Width (m)" type="number" value={e.width} onChange={(ev) => updateElement(e.id, { width: ev.target.value })} />
                <LabeledInput label="Depth (m)" type="number" value={e.depth} onChange={(ev) => updateElement(e.id, { depth: ev.target.value })} />
              </div>
              <label style={{ display: 'block', fontSize: 10, color: colors.textMuted, margin: '6px 0 2px' }}>Formed Edges (select which edges need side formwork)</label>
              {e.formedEdges.map((fe) => (
                <label key={fe.edge} style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 11 }}>
                  <input type="checkbox" checked={fe.included} onChange={() => toggleEdge(e.id, fe.edge)} /> {fe.edge}
                </label>
              ))}
            </>
          )}

          {e.elementType === 'beam' && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 6 }}>
              <LabeledInput label="Width (m)" type="number" value={e.width} onChange={(ev) => updateElement(e.id, { width: ev.target.value })} />
              <LabeledInput label="Depth (m)" type="number" value={e.depth} onChange={(ev) => updateElement(e.id, { depth: ev.target.value })} />
              <LabeledInput label="Length (m)" type="number" value={e.length} onChange={(ev) => updateElement(e.id, { length: ev.target.value })} />
            </div>
          )}

          {e.elementType === 'column' && (
            <>
              <LabeledSelect label="Shape" value={e.shape} onChange={(ev) => updateElement(e.id, { shape: ev.target.value })}>
                <option value="rectangular">Rectangular / Square</option><option value="circular">Circular</option>
              </LabeledSelect>
              {e.shape === 'circular' ? (
                <LabeledInput label="Diameter (m)" type="number" value={e.diameter} onChange={(ev) => updateElement(e.id, { diameter: ev.target.value })} />
              ) : (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
                  <LabeledInput label="Width (m)" type="number" value={e.width} onChange={(ev) => updateElement(e.id, { width: ev.target.value })} />
                  <LabeledInput label="Depth (m)" type="number" value={e.depth} onChange={(ev) => updateElement(e.id, { depth: ev.target.value })} />
                </div>
              )}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
                <LabeledInput label="Height (m)" type="number" value={e.height} onChange={(ev) => updateElement(e.id, { height: ev.target.value })} />
                <LabeledInput label="Geometry Count (No.)" type="number" value={e.geometryCount} onChange={(ev) => updateElement(e.id, { geometryCount: ev.target.value })} />
              </div>
            </>
          )}
        </div>
      ))}
      {result.value && (
        <div style={{ margin: '12px 0' }}>
          {result.value.elementBreakdown.map((eb, i) => (
            <div key={i} style={{ border: `1px solid ${colors.border}`, borderRadius: 8, padding: 10, marginBottom: 8, background: '#FAFBFC', fontSize: 11 }}>
              <strong>{eb.element.label}</strong> ({eb.element.elementType}): <strong>{formatNumber(eb.total)} m²</strong>
              {eb.geometryCount > 1 && <div>({formatNumber(eb.perInstance)} × {eb.geometryCount})</div>}
            </div>
          ))}
          <div style={{ padding: '10px 12px', background: '#F8FAFF', border: `1px solid ${colors.primary}`, borderRadius: 8, fontWeight: 700 }}>
            Total: {formatNumber(result.value.finalQuantity)} m²
          </div>
        </div>
      )}
      {result.error && <div style={{ fontSize: 12, color: colors.textMuted, fontStyle: 'italic', marginBottom: 14 }}>{result.error}</div>}
      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
        {!superseding && <button onClick={() => { if (window.confirm('Discard draft?')) { clearDraft(); onClose() } }} style={{ background: 'none', border: 'none', color: colors.danger, cursor: 'pointer', fontSize: 12 }}>Discard Draft</button>}
        <Button onClick={handleSave} disabled={!result.value || saving} style={{ marginLeft: 'auto' }}>{saving ? 'Saving…' : superseding ? 'Save New Version' : 'Save Output'}</Button>
      </div>
    </div>
  )
}
function LabeledInput({ label, ...p }) { return <div style={{marginBottom:6}}><label style={{ display: 'block', fontSize: 10, color: colors.textMuted, marginBottom: 2 }}>{label}</label><Input {...p} style={{ fontSize: 11, width: '100%' }} /></div> }
function LabeledSelect({ label, children, ...p }) { return <div style={{marginBottom:6}}><label style={{ display: 'block', fontSize: 10, color: colors.textMuted, marginBottom: 2 }}>{label}</label><Select {...p} style={{ fontSize: 11, width: '100%' }}>{children}</Select></div> }
